import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { 
  Spade, 
  Bomb, 
  Gamepad2, 
  Brain, 
  Trophy,
  Play,
  Car,
  Star,
  Sword,
  Zap,
  Download,
  RefreshCw,
  Settings,
  CheckCircle
} from 'lucide-react';
import { Solitaire } from '../games/Solitaire';
import { Minesweeper } from '../games/Minesweeper';
import { Snake } from '../games/Snake';
import { MemoryMatch } from '../games/MemoryMatch';
import { PlatformerGame } from '../games/PlatformerGame';
import { ZeldaRPG } from '../games/ZeldaRPG';
import MechWarrior from '../games/MechWarrior';
import { GameInstaller } from '../../utils/GameInstaller';
import { toast } from '../../hooks/use-toast';
import { getAssetPath } from '../../utils/assetPath';

interface Game {
  id: string;
  name: string;
  description: string;
  icon: React.ComponentType<any>;
  difficulty: string;
  category: string;
  component?: React.ComponentType<any>;
  externalAppId?: string;
  needsInstallation?: boolean;
  installationDescription?: string;
}

interface OSGamesProps {
  onClose?: () => void;
  onOpenWindow?: (appId: string, title: string, filePath?: string, content?: string) => void;
}

const JoystickIcon = ({ className = '' }: { className?: string }) => (
  <img
    src={getAssetPath('/os/assets/icons/joystick.svg')}
    alt="Retro Play"
    className={className}
    draggable={false}
  />
);

export const OSGames: React.FC<OSGamesProps> = ({ onClose, onOpenWindow }) => {
  const [selectedGame, setSelectedGame] = useState<string | null>(null);
  const [installingGames, setInstallingGames] = useState<Set<string>>(new Set());
  const [installedGames, setInstalledGames] = useState<Set<string>>(new Set());

  const games: Game[] = [
    {
      id: 'solitaire',
      name: 'Solitaire',
      description: 'Classic Klondike Solitaire card game. Build foundation piles from Ace to King.',
      icon: Spade,
      difficulty: 'Easy',
      category: 'Card',
      component: Solitaire
    },
    {
      id: 'minesweeper',
      name: 'Minesweeper',
      description: 'Find all the mines without clicking on them. Use numbers to guide your strategy.',
      icon: Bomb,
      difficulty: 'Medium',
      category: 'Puzzle',
      component: Minesweeper
    },
    {
      id: 'snake',
      name: 'Snake',
      description: 'Control the snake to eat food and grow longer. Don\'t hit the walls or yourself!',
      icon: Gamepad2,
      difficulty: 'Medium',
      category: 'Arcade',
      component: Snake
    },
    {
      id: 'memory',
      name: 'Memory Match',
      description: 'Flip cards to find matching pairs. Challenge your memory and improve your score.',
      icon: Brain,
      difficulty: 'Easy',
      category: 'Memory',
      component: MemoryMatch
    },
    {
      id: 'universal-emulator',
      name: 'Retro Play',
      description: 'Launch the retro emulator app with support for classic systems like NES, SNES, Game Boy, Game Boy Advance, and Nintendo 64.',
      icon: JoystickIcon,
      difficulty: 'Medium',
      category: 'Emulation',
      externalAppId: 'universal-emulator'
    },
    {
      id: 'grand-theft-boxy',
      name: 'Grand Theft Boxy',
      description: 'Cruise a compact neon city, evade patrol cars, and launch the boxy sandbox in its own MapleOS window.',
      icon: Car,
      difficulty: 'Medium',
      category: 'Driving',
      externalAppId: 'grand-theft-boxy'
    },
    {
      id: 'platformer',
      name: 'MapleOS Platformer',
      description: 'Classic 2D side-scrolling adventure with procedural levels, enemies, and collectibles!',
      icon: Gamepad2,
      difficulty: 'Hard',
      category: 'Adventure',
      component: PlatformerGame
    },
    {
      id: 'zelda-rpg',
      name: 'MapleOS Quest',
      description: 'Epic top-down RPG adventure! Explore mystical forests, battle enemies, solve puzzles, and rescue the princess!',
      icon: Sword,
      difficulty: 'Hard',
      category: 'RPG',
      component: ZeldaRPG
    },
  ];

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'Easy': return 'text-green-600 bg-green-100';
      case 'Medium': return 'text-yellow-600 bg-yellow-100';
      case 'Hard': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'Card': return 'text-purple-600 bg-purple-100';
      case 'Puzzle': return 'text-blue-600 bg-blue-100';
      case 'Arcade': return 'text-orange-600 bg-orange-100';
      case 'Memory': return 'text-pink-600 bg-pink-100';
      case 'Adventure': return 'text-green-600 bg-green-100';
      case 'RPG': return 'text-red-600 bg-red-100';
      case 'Simulation': return 'text-purple-600 bg-purple-100';
      case 'Emulation': return 'text-cyan-700 bg-cyan-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const handleGameSelect = (gameId: string) => {
    const game = games.find((entry) => entry.id === gameId);
    if (!game) {
      return;
    }

    if (game.externalAppId) {
      onOpenWindow?.(game.externalAppId, game.name);
      return;
    }

    setSelectedGame(gameId);
  };

  const handleBackToGames = () => {
    setSelectedGame(null);
  };

  const handleInstallGame = async (gameId: string) => {
    setInstallingGames(prev => new Set(prev).add(gameId));
    
    try {
      if (gameId === 'mt-mek') {
        const installer = new GameInstaller();
        toast({
          title: "Installing MT-MEK...",
          description: "Installing mech models and game assets. This may take a moment.",
        });
        
        // Add realistic installation delay to show progress
        await new Promise(resolve => setTimeout(resolve, 2000));
        await installer.installMekWarrior();
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        setInstalledGames(prev => new Set(prev).add(gameId));
        
        toast({
          title: "MT-MEK Installed!",
          description: "All mech models and assets have been installed successfully.",
        });
      }
    } catch (error) {
      console.error('Installation failed:', error);
      toast({
        title: "Installation Failed",
        description: "There was an error installing the game. Please try again.",
        variant: "destructive",
      });
    } finally {
      setInstallingGames(prev => {
        const newSet = new Set(prev);
        newSet.delete(gameId);
        return newSet;
      });
    }
  };

  if (selectedGame) {
    const game = games.find(g => g.id === selectedGame);
    if (game?.component) {
      const GameComponent = game.component;
      return <GameComponent onClose={handleBackToGames} />;
    }
  }

  return (
    <div className="w-full h-full bg-background p-6 overflow-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-8 bg-card/70 backdrop-blur-sm rounded-xl p-6 border shadow-lg">
        <div className="flex items-center gap-4">
          <div className="p-3 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 shadow-lg">
            <Trophy className="h-8 w-8 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              MapleOS Games
            </h1>
            <p className="text-muted-foreground">
              Classic office games for your entertainment
            </p>
          </div>
        </div>
        
        {onClose && (
          <Button
            variant="outline"
            onClick={onClose}
            className="hover:bg-muted/50"
          >
            Close
          </Button>
        )}
      </div>

      {/* Games Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
        {games.map((game) => {
          const IconComponent = game.icon;
          const isInstalling = installingGames.has(game.id);
          const isInstalled = installedGames.has(game.id);
          const needsInstall = game.needsInstallation && !isInstalled;
          
          return (
            <Card 
              key={game.id}
              className="group cursor-pointer transition-all duration-300 hover:scale-[1.02] hover:shadow-xl bg-card/70 backdrop-blur-sm"
              onClick={() => !needsInstall && handleGameSelect(game.id)}
            >
              <CardHeader className="pb-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-4">
                    <div className="p-3 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 shadow-lg group-hover:shadow-xl transition-shadow">
                      <IconComponent className="h-8 w-8 text-white" />
                    </div>
                    <div>
                      <CardTitle className="text-xl mb-1 flex items-center gap-2">
                        {game.name}
                        {isInstalled && (
                          <CheckCircle className="h-4 w-4 text-green-600" />
                        )}
                      </CardTitle>
                      <div className="flex gap-2 mb-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(game.difficulty)}`}>
                          {game.difficulty}
                        </span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(game.category)}`}>
                          {game.category}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <CardDescription className="text-sm leading-relaxed">
                  {game.description}
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-0">
                {needsInstall ? (
                  <div className="space-y-3">
                    <div className="text-xs text-muted-foreground bg-muted/50 rounded-lg p-3">
                      <strong>Installation Required:</strong> {game.installationDescription}
                    </div>
                    <Button 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleInstallGame(game.id);
                      }}
                      disabled={isInstalling}
                      className="w-full"
                    >
                      {isInstalling ? (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                          Installing...
                        </>
                      ) : (
                        <>
                          <Download className="h-4 w-4 mr-2" />
                          Install Game
                        </>
                      )}
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Play className="h-4 w-4" />
                      Click to play
                    </div>
                    <Button size="sm" variant="outline" className="group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                      Launch
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};